export const ODataPeriod = {
  DAY: "N",
  MONTH: "T",
  QUARTER: "Q",
} as const;

export type DataPeriod = (typeof ODataPeriod)[keyof typeof ODataPeriod];

export const OInvoiceSummaryField = {
  INVOICE_ID: "invoiceId",
  INVOICE_NUMBER: "invoiceNumber",
  INVOICE_SERIAL: "invoiceSerial",
  INVOICE_DATE: "invoiceDate",
  STATUS: "status",
  CREATION_DATE: "creationDate",
  IS_FIRST_SUBMISSION: "isFirstSubmission",
  TOTAL_SUBMIT: "supplementTimes",
  DATA_PERIOD: "dataPeriod",
  DATA_PERIOD_DATE: "dataPeriodDate",
  MONTH_OR_QUARTER: "monthOrQuarter",
  YEAR: "year",
  SUMMARY_DATA_NUMBER: "summaryDataNumber",
  UPDATE_COUNT: "updateCount",
  CURRENCY_CODE: "currencyCode",
  CREATOR: "creatorName",
  SIGN_DATE: "signDate",
  NOTE: "note",
  GOODS_TYPE: "goodsType",
} as const;

export type InvoiceSummaryFieldType =
  (typeof OInvoiceSummaryField)[keyof typeof OInvoiceSummaryField];

export const OInvoiceSummaryStatus = {
  WAIT_APPROVE: 0,
  WAIT_SIGN: 1,
  SIGNED: 2,
  SENT: 3,
  SUCCESSFUL: 4,
  ERROR: 5,
  REFUSE: 6,
} as const;

export type InvoiceSummaryStatusType =
  (typeof OInvoiceSummaryStatus)[keyof typeof OInvoiceSummaryStatus];
export const OPrepareSummaryStatus = {
  ORIGIN: 0,
  REFUSE: 1,
  ADJUSTING: 2,
  REPLACING: 3,
  EXPLAINING: 4,
  ADJUSTED: 5,
  REPLACED: 6,
  ERROR: 7,
  NOTIFYING: 8,
} as const;

export type PrepareSummaryStatus =
  (typeof OPrepareSummaryStatus)[keyof typeof OPrepareSummaryStatus];

export const prepareInvoiceStatuses = [
  { value: OPrepareSummaryStatus.ORIGIN, label: "Gốc" },
  { value: OPrepareSummaryStatus.REFUSE, label: "Hủy" },
  { value: OPrepareSummaryStatus.ADJUSTING, label: "Điều chỉnh" },
  { value: OPrepareSummaryStatus.REPLACING, label: "Thay thế" },
  { value: OPrepareSummaryStatus.EXPLAINING, label: "Giải trình" },
  { value: OPrepareSummaryStatus.ADJUSTED, label: "Bị điều chỉnh" },
  { value: OPrepareSummaryStatus.REPLACED, label: "Bị thay thế" },
  { value: OPrepareSummaryStatus.ERROR, label: "Sai sót do tổng hợp" },
  { value: OPrepareSummaryStatus.NOTIFYING, label: "Thông báo" },
];

export const OInvoiceLookUpField = {
  INVOICE_ID: "invoiceId",
  INVOICE_NUMBER: "invoiceNumber",
  INVOICE_SERIAL: "invoiceSerial",
  INVOICE_DATE: "invoiceDate",
  BUYER_TAX_CODE: "buyerTaxCode",
  BUYER_NAME: "buyerName",
  TOTAL_AMOUNT: "totalAmount",
  DATE_SIGN: "dateSign",
  CREATION_DATE: "creationDate",
  CREATOR_NAME: "creatorName",
  EXCHANGE_RATE: "exchangeRate",
  TOTAL_FEE: "totalFee",
  TOTAL_DISCOUNT: "totalDiscount",
  CREATE_TIME: "createTime",
  BUYER_BUDGET_UNIT_CODE: "buyerBudgetUnitCode",
  PRODUCT_CODE: "productCode",
  PRODUCT_NAME: "productName",
  AMOUNT_WITHOUT_VAT: "amountWithoutVAT",
  TOTAL_DISCOUNT_AMOUNT: "totalDiscountAmount",
  VAT_RATE: "vatRate",
  UNIT_CODE: "unitCode",
  VAT_AMOUNT: "vatAmount",
  NOTE: "note",
  INVOICE_STATUS: "invoiceStatus",
  QUANTITY: "quantity",
  RELATED_INVOICE_NUMBER: "relatedInvoiceNumber",
  RELATED_INVOICE_SERIAL: "relatedInvoiceSerial",
  RELATED_INVOICE_DATE: "relatedInvoiceDate",
  RELATED_TEMPLATE_CODE: "relatedTemplateCode",
  RELATED_INVOICE_TYPE: "relatedInvoiceType",
  ADJUSTMENT_PERIOD_TYPE: "adjustmentPeriodType",
  ADJUSTMENT_PERIOD_VALUE: "adjustmentPeriodValue",
} as const;

export type InvoiceLookUpFieldType =
  (typeof OInvoiceLookUpField)[keyof typeof OInvoiceLookUpField];

export const OInvoiceGoodsType = {
  FLIGHT_GOODS: 2,
  OTHERS: 9,
};
export type InvoiceGoodsType =
  (typeof OInvoiceGoodsType)[keyof typeof OInvoiceGoodsType];

export interface SummaryDataDetails {
  invoiceId: string;
  invoiceDetailId: string;
  invoiceSerial: string;
  invoiceNumber: number;
  invoiceDate: string;
  buyerName: string;
  exchangeRate: number;
  totalFee: number;
  totalDiscountAmount: number;
  amountWithoutVAT: number;
  vatRate: number;
  vatAmount: number;
  totalAmount: number;
  note: string;
  invoiceStatus: number;
  BuyerBudgetUnitCode?: string;
  id: string;
}

export interface InvoiceSummary {
  id: string;
  status: InvoiceSummaryStatusType;
  isFirstSubmission: boolean;
  dataPeriod: string;
  dataPeriodDate?: string;
  monthOrQuarter: number;
  year: number;
  summaryDataNumber: number;
  currencyCode: string;
  signDate: string;
  note: string;
  goodsType: InvoiceGoodsType;
  supplementTimes?: number;
  creationDate: string;
}

export interface CreateInvoiceSummaryBody {
  dataPeriod: string;
  dataPeriodDate?: string;
  monthOrQuarter?: number;
  year?: number;
  creationDate: string;
  currencyCode: string;
  note: string;
  summaryDataDetails: SummaryDataDetails[];
  supplementTimes?: number;
  summaryDataNumber?: number;
  goodsType?: InvoiceGoodsType;
}
export interface UpdateInvoiceSummaryBody {
  dataPeriod: string;
  dataPeriodDate?: string;
  monthOrQuarter?: number;
  year: number;
  creationDate: string;
  currencyCode: string;
  note: string;
  summaryDataDetails: SummaryDataDetails[];
  supplementTimes?: number;
  goodsType?: InvoiceGoodsType;
}

export interface InvoiceSummaryLog {
  summaryLogType: string;
  errorCode: string;
  errorDescription: string;
  receivingDate: string;
  messageReferenceCode: string;
}

export interface ListResponse<ItemType> {
  items: ItemType[];
  totalCount: number;
}
export interface CreateInvoiceSummaryFormState {
  dataPeriod: DataPeriod;
  dataPeriodDate: string;
  monthOrQuarter: string;
  year: string;
  status: string;
  isFirstSubmission: boolean;
  currencyCode: string;
  type: string;
  multiTax: boolean;
  totalSubmit?: number;
  creationDate: string;
  summaryDataNumber?: string;
  goodsType?: InvoiceGoodsType;
}

export interface UpdateInvoiceSummaryFormState {
  dataPeriod: DataPeriod;
  dataPeriodDate: string;
  monthOrQuarter: string;
  year: string;
  isFirstSubmission: boolean;
  currencyCode: string;
  type: string;
  multiTax: boolean;
  totalSubmit?: number;
  creationDate: string;
  summaryDataNumber?: string;
  supplementTimes?: number;
  goodsType?: InvoiceGoodsType;
}

export interface GetValidSerialsRes {
  id: string;
  invoiceSerial: string;
  invoiceTypeName: string;
}
